import { inject, Injectable } from '@angular/core';
import { WarehouseReportErrorApiService } from './api/warehouse-report-error-api.service';
import { DBService } from './db.service';
import { FileService } from './file.service';
import { appDb } from 'dexie/app.db';
import { liveQuery } from 'dexie';
import {
  catchError,
  combineLatest,
  from,
  of,
  switchMap,
  throwError,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { generateUUID } from 'helpers/string.helper';
import { CacheKeyService } from './cache-key.service';

@Injectable({
  providedIn: 'root',
})
export class WarehouseReportErrorService {
  protected dbService = inject(DBService);
  protected fileService = inject(FileService);
  protected cacheKeyService = inject(CacheKeyService);
  protected warehouseReportErrorApiService = inject(
    WarehouseReportErrorApiService
  );

  getWarehouseReportErrors(warehouseReportId: string) {
    let dbQuery = appDb.warehouseReportError
      .where(['isDeleted', 'wareHouseReportId'])
      .equals([0, warehouseReportId]);
    return from(liveQuery(() => dbQuery.toArray()));
  }

  get(id: string) {
    return from(appDb.warehouseReportError.get(id));
  }

  create(data, images?: Array<string | Blob>) {
    const isOnline = navigator.onLine;
    let id = null;
    if (!isOnline) {
      id = generateUUID();
    }
    const createDate = new Date();
    return (
      images?.length
        ? combineLatest([
            ...images.map((image, i) => {
              if (!(image instanceof Blob)) {
                return of(image);
              }
              return this.fileService.uploadFile(image, {
                type: 'warehouseReportError',
                id,
                replaceWith: `images.${i}`,
              });
            }),
          ])
        : of(null)
    ).pipe(
      switchMap((images) =>
        this.warehouseReportErrorApiService
          .create({ ...data, id, images, createDate })
          .pipe(
            catchError((err: HttpErrorResponse) => {
              if (!navigator.onLine) {
                return this.dbService.createRow(appDb.warehouseReportError, {
                  ...data,
                  images,
                  createDate,
                  isSynced: 0,
                  isDeleted: 0,
                });
              }
              return throwError(() => err);
            })
          )
      )
    );
  }
  update(id, data, images?: Array<string | Blob>) {
    return (
      images?.length
        ? combineLatest([
            ...images.map((image, i) => {
              if (!(image instanceof Blob)) {
                return of(image);
              }
              return this.fileService.uploadFile(image, {
                type: 'warehouseReportError',
                id,
                replaceWith: `images.${i}`,
              });
            }),
          ])
        : of(null)
    ).pipe(
      switchMap((images) =>
        this.warehouseReportErrorApiService
          .update(id, { ...data, id, images })
          .pipe(
            catchError((err: HttpErrorResponse) => {
              if (!navigator.onLine) {
                return this.dbService.updateRow(appDb.warehouseReportError, {
                  ...data,
                  id,
                  images,
                  isSynced: 0,
                  isDeleted: 0,
                });
              }
              return throwError(() => err);
            })
          )
      )
    );
  }
  delete(id) {
    return this.warehouseReportErrorApiService.delete(id).pipe(
      switchMap((res: any) =>
        this.dbService.deleteRow(appDb.warehouseReportError, id)
      ),
      catchError((err: HttpErrorResponse) => {
        if (!navigator.onLine) {
          return this.dbService.updateRow(appDb.warehouseReport, {
            id,
            isDeleted: 1,
            isSynced: 0,
          });
        }
        return throwError(() => err);
      })
    );
  }

  bulkCreate(data) {
    return this.warehouseReportErrorApiService
      .bulk(
        data.map((item) => ({
          ...item,
          isRepaired: item.isRepaired ? true : false,
          isDeleted: item.isDeleted ? true : false,
        }))
      )
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportError').pipe(
            switchMap((cache) => {
              return this.warehouseReportErrorApiService.getList(cache.hash);
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService.checkAndUpdateTables('wareHouseReportError')
        ),
        catchError((err) => {
          if (!navigator.onLine) {
            return this.dbService.bulkPutRow(
              appDb.warehouseReportError,
              data.map((item) => ({ ...item, isSynced: 0 }))
            );
          }
          return throwError(() => err);
        })
      );
  }

  move(data: {
    wareHouseReportErrorId: string;
    fromWareHouseReportId: string;
    toWareHouseReportId: string;
  }) {
    return this.warehouseReportErrorApiService
      .move(
        data.wareHouseReportErrorId,
        data.fromWareHouseReportId,
        data.toWareHouseReportId
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (!navigator.onLine) {
            return this.dbService.updateRow(appDb.warehouseReport, {
              id: data.wareHouseReportErrorId,
              wareHouseId: data.toWareHouseReportId,
              ...data,
              isSynced: 0,
            });
          }
          return throwError(() => err);
        })
      );
  }

  changeRepairedStatus(wareHouseReportErrorId, isRepaired) {
    return this.warehouseReportErrorApiService
      .changeRepairedStatus(wareHouseReportErrorId, isRepaired ? true : false)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (!navigator.onLine) {
            return this.dbService.updateRow(appDb.warehouseReportError, {
              id: wareHouseReportErrorId,
              isRepaired,
              isSynced: 0,
              isDeleted: 0,
            });
          }
          return throwError(() => err);
        })
      );
  }

  getNotSyncedItems() {
    return from(
      appDb.warehouseReportError.where('isSynced').equals(0).toArray()
    );
  }

  getNotSyncedItemsCount() {
    return from(
      liveQuery(() =>
        appDb.warehouseReportError.where('isSynced').equals(0).count()
      )
    );
  }

  bulkUpdateLocal(items) {
    return from(appDb.warehouseReportError.bulkPut(items));
  }

  localUpdate(id, data, isSynced = false) {
    return from(
      appDb.warehouseReportError.update(id, {
        ...data,
        isSynced: isSynced ? 1 : 0,
      })
    );
  }
}
